import { Product } from '@portal/portal-core';

interface VersionValidationResult {
  allowed: boolean;
  error?: string;
}

/**
 * Validates if a product version can be created based on business rules:
 * - All versions of FACS Workflow Manager are not allowed
 * - FlowJo versions less than 11.0.0 are not allowed
 * - All versions of SeqGeq are not allowed
 */
export const validateProductVersionCreation = (
  productName: string,
  semanticVersion: string,
): VersionValidationResult => {
  // Block all FACS Workflow Manager versions
  if (productName === Product.NAMES.facsWorkflowManager) {
    return {
      allowed: false,
      error: 'Product versions cannot be created for BD FACS™ Workflow Manager',
    };
  }

  // Block all SeqGeq versions
  if (productName === Product.NAMES.seqGeq) {
    return {
      allowed: false,
      error: 'Product versions cannot be created for SeqGeq™',
    };
  }

  // Block FlowJo versions less than 11.0.0
  if (productName === Product.NAMES.flowJo) {
    const versionParts = semanticVersion.split('.').map((v) => parseInt(v, 10));
    const major = versionParts[0] || 0;

    // Check if version is less than 11.0.0
    if (major < 11) {
      return {
        allowed: false,
        error: `Product versions for FlowJo™ must be version 11.0.0 or higher (provided: ${semanticVersion})`,
      };
    }

    // Version 11.0.0 and above is allowed
    return { allowed: true };
  }

  // All other products are allowed
  return { allowed: true };
};

/**
 * Determines if a product version should be considered active/enabled.
 * A product version is active if:
 * - active property is explicitly true (v2 schema)
 * - isActive property is explicitly true (v1 schema)
 * - active property is undefined (backward compatibility for products without the field)
 * - active property is null (treated as not explicitly disabled)
 *
 */
export const isProductVersionActive = (
  product: Record<string, unknown> | null | undefined,
): boolean => {
  if (!product) {
    return false;
  }
  // Check v2 schema (active) or v1 schema (isActive)
  const activeStatus = product.active !== undefined ? product.active : product.isActive;
  return activeStatus === true || activeStatus === undefined || activeStatus === null;
};
